// 261005 E3-P1b — MỌI quyết định hiển thị của thẻ khối chặng ② Khung xương (hàm thuần, có jest). KHÔNG import antd/react.
// Khối ② "Sẵn sàng Áp ngay": BE tính xem thử ($0) ngay khi AI trả lời ⇒ thẻ hiện sẵn diff + ảnh hưởng, người dùng chỉ bấm Áp.
import type { AssistantBlock, SkeletonBlockDiff, SkeletonIssueChip } from '../../../../types/adaptationAssistant';
import { FIX_LABEL, type AssistantRequest } from './assistantIntent';
import { AUTO_UNDO_DRAFT_HINT, autoApplyNote, type AutoApplyNote } from './autoApplyView';
import { friendlyJobNote } from '../jobErrorText';
import { isOverviewAssistantOp, OVERVIEW_APPLY_HINT, OVERVIEW_ASSISTANT_OP_LABEL } from '../overview/overviewAssist';

const str = (v: unknown): string => String(v ?? '').trim();
const UNDO_EPS_MS = 1;

export type SkeletonPhase = 'proposed' | 'ready' | 'stale' | 'failed' | 'applied' | 'restored' | 'dismissed' | 'running';

export interface RejectedLine { label: string; reason: string }

export interface SkeletonCardView {
  phase: SkeletonPhase;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  headline: string;
  /** Mô tả phụ (lý do của AI / gợi ý theo trạng thái). */
  detail: string;
  /** Danh sách lệnh (nhãn VN, theo thứ tự). */
  opLabels: string[];
  /** "Lệnh không áp được": gộp op bị normalizer loại + op bị loại lúc xem thử. */
  rejected: RejectedLine[];
  /** Cảnh báo "Danh sách tập sẽ cần chia lại ở ④" (chỉ khi khối này là thủ phạm bật cờ). */
  staleWarning: string | null;
  /** Các dòng ảnh hưởng tới tập đã viết. */
  impactLines: string[];
  /** 261015 P2b-1 (Q1/D2): dòng "tập đã viết bị ảnh hưởng" của gieo / trích khoảnh khắc — BE dựng chữ (`diff.anchorImpact` / `appliedResult.anchorImpact`), FE chỉ in. */
  anchorImpactLines: string[];
  /** 261015 P2b-2: `ok` khi mọi mục khoảnh khắc đều `verdict: quote` (tập đã viết vẫn chứng minh được) — thẻ không cần màu cảnh báo; còn lại `warn`. */
  anchorImpactTone: 'ok' | 'warn';
  /** `diff.applied[i].detail` của 4 op mới (BE dựng "chương N, tập K") — nhãn `command.labels` không thể chứa chúng. */
  detailLines: string[];
  /** Áp khối này GIỮ "đã duyệt" (truyện đang APPROVED ∧ mọi op thuộc lớp sửa-chỉ-metadata-có-neo). */
  keepsApproval: boolean;
  /** Có `setBeatPlant` origin ADDED: huy hiệu "do chuyển thể thêm" + câu "sẽ cần duyệt lại"; null ⇒ không có. */
  addedNote: string | null;
  /** Mô tả trong hộp xác nhận "Áp đề xuất này?" — nói đúng giữ duyệt / về nháp (chuỗi cứng cũ nói sai sau C5). */
  applyHint: string;
  canApply: boolean;
  canPreview: boolean;
  previewLabel: string;
  canUndo: boolean;
  undoReason: string | null;
  /** Tooltip của nút "Khôi phục bản trước": nêu luật cờ chia tập. */
  undoHint: string;
  canReapply: boolean;
  canDismiss: boolean;
  /** 261015 P5a (D5): "Đã tự áp…" / "Chưa tự áp: …" — null khi không có dấu. */
  autoNote: AutoApplyNote | null;
}

export const UNDO_CHANGED_TEXT_SKELETON = 'Khung xương đã đổi sau khi áp — khôi phục qua mục Phiên bản';
export const STALE_OTHER_APPLIED_TEXT = 'Đã áp một đề xuất khác hoặc khung xương đã đổi — xem thử lại để thấy tác động mới';
export const EPISODES_STALE_TEXT = 'Danh sách tập sẽ cần chia lại ở ④ (bước chia tập)';
export const UNDO_FLAG_HINT = 'Khung xương về bản trước khi áp. Cờ "chia tập cần làm lại" chỉ tắt lại nếu chính khối này đã bật nó; mọi trường hợp khác giữ cờ. Khôi phục bản trước luôn đưa khung xương về bản nháp (cần duyệt lại).';
export const KEEPS_APPROVAL_HINT = 'Khung xương giữ trạng thái đã duyệt (chỉ sửa metadata có neo nguồn); bản cũ còn ở mục Phiên bản.';
export const DRAFT_ON_APPLY_HINT = 'Khung xương về bản nháp (cần duyệt lại); bản cũ còn ở mục Phiên bản.';
export const ADDED_NOTE_TEXT = 'Chi tiết gieo do chuyển thể thêm (không có trong nguyên tác) — khung xương sẽ cần duyệt lại.';
/** 4 op của P2b-1 + `addBeatFromMaterial` (P2b-3) — chỉ các op này được in thêm `detail` (op cũ đã đủ nhãn). */
export const ANCHOR_OP_KINDS: readonly string[] = ['setBeatPlant', 'editRequiredMoment', 'acceptWarning', 'revokeAcceptance', 'addBeatFromMaterial',
  // 261015 P4c-1: 4 lệnh tổng quan — BE dựng `detail` ("sửa ý v1c3: đã tìm thấy trích / chưa xác minh (…)", "thêm quan hệ …") mà nhãn `command.labels` không chứa được.
  'setOverviewClaim', 'removeOverviewClaim', 'upsertOverviewIntroLine', 'upsertOverviewRelationship'];
/** Mục `anchorImpact` mà tập ĐÃ viết phải xem lại (khác "sẽ vào lời dặn khi viết"). */
/** KHOÁ LITERAL cùng danh sách `WRITTEN_ANCHOR_KINDS` của BE (`utils/anchorImpact.ts`; FE không import được BE ⇒ test hai phía cùng khoá). `PLANT_NOT_YET` cố ý KHÔNG có. */
export const WRITTEN_ANCHOR_KINDS: readonly string[] = ['PLANT_WRITTEN', 'PAYOFF_BEFORE_PLANT', 'MOMENT_RECLAIM', 'MOMENT_VERDICT', 'BEAT_ADDED_WRITTEN'];   // 261015 P2b-3: + cao trào mới thêm vào tập đã viết

export const skeletonDiffOf = (block: Pick<AssistantBlock, 'diff'>): SkeletonBlockDiff | null => {
  const d = block.diff as unknown as Partial<SkeletonBlockDiff> | null;
  if (!d || typeof d !== 'object' || !Array.isArray(d.applied)) return null;
  return {
    applied: d.applied, rejected: Array.isArray(d.rejected) ? d.rejected : [], violations: Array.isArray(d.violations) ? d.violations : [],
    episodesStale: d.episodesStale === true, staleBefore: d.staleBefore === true, impact: Array.isArray(d.impact) ? d.impact : [],
    anchorImpact: Array.isArray(d.anchorImpact) ? d.anchorImpact : [], keepsApproval: d.keepsApproval === true,
  };
};

/** `diff.applied[].detail` của 4 op mới. */
export const appliedDetailLinesOf = (diff: Pick<SkeletonBlockDiff, 'applied'> | null | undefined): string[] =>
  (diff?.applied ?? []).filter((a) => ANCHOR_OP_KINDS.includes(a.op) && str(a.detail)).map((a) => str(a.detail));

/** 261015 P2b-2: có ít nhất một mục và TẤT CẢ đều `verdict: quote` ⇒ `ok`. Mục không có verdict (BE cũ / loader lỗi) ⇒ `warn` (không giả vờ đã kiểm). */
export const anchorImpactTone = (list: SkeletonBlockDiff['anchorImpact'] | undefined): 'ok' | 'warn' =>
  list && list.length > 0 && list.every((x) => x.verdict === 'quote') ? 'ok' : 'warn';

/** Chữ BE dựng, giữ thứ tự. */
export const anchorImpactLinesOf = (list: SkeletonBlockDiff['anchorImpact'] | undefined): string[] => (list ?? []).map((x) => str(x.text)).filter(Boolean);

/** Nhãn lệnh: `command.labels` (BE); thiếu ⇒ tên op. */
export const opLabelsOf = (block: Pick<AssistantBlock, 'command'>): string[] => {
  const ops = block.command?.ops ?? [];
  const labels = block.command?.labels ?? [];
  return ops.map((o, i) => str(labels[i]) || OVERVIEW_ASSISTANT_OP_LABEL[str(o.op)] || str(o.op));
};

/** Lệnh bị loại: normalizer (`command.rejectedOps`) + xem thử (`diff.rejected`). */
export const rejectedLinesOf = (block: Pick<AssistantBlock, 'command' | 'diff'>): RejectedLine[] => [
  ...(block.command?.rejectedOps ?? []).map((r) => ({ label: str(r.op), reason: str(r.reason) })),
  ...(skeletonDiffOf(block)?.rejected ?? []).map((r) => ({ label: str(r.op), reason: str(r.reason) })),
];

/** "Tập 1 (cảnh 2): Kamil, Hội trường sẽ không còn" — `nameOf` tra tên từ khung xương hiện tại (id lạ ⇒ chính id). */
export const impactLinesOf = (impact: SkeletonBlockDiff['impact'] | undefined, nameOf: (id: string) => string = (x) => x): string[] =>
  (impact ?? []).flatMap((it) => it.scenes.map((sc) => {
    const ids = [...sc.characterIds, ...sc.locationIds].map(nameOf).join(', ');
    return `Tập ${it.episode} (cảnh ${sc.index}): ${ids} sẽ không còn trong khung xương`;
  }));

const undoState = (block: AssistantBlock, headModifiedAt: string | null | undefined, busy: boolean, busyReason?: string | null): { canUndo: boolean; undoReason: string | null } => {
  if (busy) return { canUndo: false, undoReason: busyReason || 'Đang có việc khác chạy — chờ xong rồi khôi phục.' };
  const applied = Date.parse(str(block.appliedResult?.modifiedAt));
  const head = Date.parse(str(headModifiedAt));
  if (Number.isNaN(head)) return { canUndo: false, undoReason: 'Đang tải khung xương hiện tại…' };
  if (Number.isNaN(applied) || Math.abs(head - applied) > UNDO_EPS_MS) return { canUndo: false, undoReason: UNDO_CHANGED_TEXT_SKELETON };
  return { canUndo: true, undoReason: null };
};

/**
 * Thẻ khối ②. `busy` ⇒ mọi nút GHI (Áp, Xem thử lại, Khôi phục, Áp lại) tắt; "Bỏ" không bị khoá.
 * Xem thử ĐỒNG BỘ: không có nút "Xem thử" ở READY; PROPOSED (xem thử tự động chưa chạy được) / STALE / FAILED / RESTORED ⇒ "Xem thử lại" ($0, không poll).
 */
export const skeletonCardView = (
  block: AssistantBlock, busy: boolean, headModifiedAt?: string | null, busyReason?: string | null, nameOf?: (id: string) => string,
): SkeletonCardView => {
  const diff = skeletonDiffOf(block);
  const rationale = str(block.command?.rationale);
  const base = {
    opLabels: opLabelsOf(block), rejected: rejectedLinesOf(block), staleWarning: null as string | null, impactLines: [] as string[],
    anchorImpactLines: [] as string[], anchorImpactTone: 'warn' as 'ok' | 'warn', detailLines: [] as string[], keepsApproval: false, addedNote: null as string | null, applyHint: DRAFT_ON_APPLY_HINT,
    canApply: false, canPreview: false, previewLabel: 'Xem thử lại', canUndo: false, undoReason: null as string | null, undoHint: UNDO_FLAG_HINT,
    canReapply: false, canDismiss: false, detail: rationale ? `Lý do: ${rationale}` : '', autoNote: autoApplyNote(block),
  };
  switch (block.state) {
    case 'PROPOSED':
      return { ...base, phase: 'proposed', tone: 'neutral', headline: 'Chưa xem thử', canPreview: !busy, previewLabel: 'Xem thử', canDismiss: true };
    case 'PREVIEW_RUNNING':
      return { ...base, phase: 'running', tone: 'neutral', headline: 'Đang xem thử…', canDismiss: true };   // 261015: BE cho DISMISS từ PREVIEW_RUNNING — khối kẹt phải bỏ được
    case 'FAILED':
      return { ...base, phase: 'failed', tone: 'danger', headline: 'Xem thử lỗi', detail: friendlyJobNote(str(block.error)) || base.detail, canPreview: !busy, canDismiss: true };
    case 'STALE':
      return { ...base, phase: 'stale', tone: 'warn', headline: 'Xem thử đã cũ', detail: STALE_OTHER_APPLIED_TEXT, canPreview: !busy, canDismiss: true };
    case 'READY': {
      const staleBefore = block.baseRef?.staleBefore === true || diff?.staleBefore === true;
      const stale = !!diff?.episodesStale && !staleBefore;
      const hasCmd = (block.command?.ops ?? []).length > 0;
      const keepsApproval = diff?.keepsApproval === true;
      const hasOverviewOp = (block.command?.ops ?? []).some((o) => isOverviewAssistantOp(str(o.op)));   // 261015 P4c-1: sửa tổng quan luôn về nháp, không đụng tập/kịch bản
      const hasAdded = (block.command?.ops ?? []).some((o) => o.op === 'setBeatPlant' && (o as { origin?: unknown }).origin === 'ADDED');
      const anchorWritten = (diff?.anchorImpact ?? []).some((x) => WRITTEN_ANCHOR_KINDS.includes(x.kind) && x.verdict !== 'quote');
      return {
        ...base, phase: 'ready', tone: stale || (diff?.impact?.length ?? 0) > 0 || anchorWritten ? 'warn' : 'ok', headline: 'Sẵn sàng áp',
        staleWarning: stale ? EPISODES_STALE_TEXT : null, impactLines: impactLinesOf(diff?.impact, nameOf),
        anchorImpactLines: anchorImpactLinesOf(diff?.anchorImpact), anchorImpactTone: anchorImpactTone(diff?.anchorImpact), detailLines: appliedDetailLinesOf(diff),
        keepsApproval, addedNote: hasAdded ? ADDED_NOTE_TEXT : null, applyHint: keepsApproval ? KEEPS_APPROVAL_HINT : hasOverviewOp ? OVERVIEW_APPLY_HINT : DRAFT_ON_APPLY_HINT,
        canApply: !busy && !!diff && hasCmd, canDismiss: true,
      };
    }
    case 'APPLIED': {
      const v = block.appliedResult?.versionNo;
      const u = undoState(block, headModifiedAt, busy, busyReason);
      const kept = block.appliedResult?.keptApproval === true;
      return {
        ...base, phase: 'applied', tone: 'ok', headline: typeof v === 'number' ? `Đã áp (v${v})` : 'Đã áp',
        detail: kept ? 'Bản cũ vẫn còn ở mục Phiên bản. Khung xương giữ trạng thái đã duyệt.' : 'Bản cũ vẫn còn ở mục Phiên bản. Khung xương về bản nháp (cần duyệt lại).',
        anchorImpactLines: anchorImpactLinesOf(block.appliedResult?.anchorImpact), anchorImpactTone: anchorImpactTone(block.appliedResult?.anchorImpact), keepsApproval: kept,
        staleWarning: block.appliedResult?.episodesStale && block.appliedResult?.staleBefore === false ? EPISODES_STALE_TEXT : null, ...u,
        // 261015 P5a (D5): bản do máy tự áp — nói thẳng khôi phục đưa khung xương về nháp (undo ② luôn ra DRAFT).
        ...(base.autoNote?.kind === 'applied' ? { undoHint: `${AUTO_UNDO_DRAFT_HINT} ${UNDO_FLAG_HINT}` } : {}),
      };
    }
    case 'RESTORED': {
      const rv = block.appliedResult?.restoreVersionNo;
      return {
        ...base, phase: 'restored', tone: 'neutral', headline: typeof rv === 'number' ? `Đã gỡ khỏi khung xương (v${rv})` : 'Đã gỡ khỏi khung xương',
        detail: '"Áp lại" không tốn AI khi phần khung xương chưa khác lúc đề xuất; nếu đã khác thì cần "Xem thử lại".',
        canPreview: !busy, previewLabel: 'Xem thử lại', canReapply: !busy, canDismiss: true,
      };
    }
    default:
      return { ...base, phase: 'dismissed', tone: 'neutral', headline: 'Đã bỏ', detail: '' };
  }
};

// ── "Nhờ AI sửa" từ danh sách vi phạm ở ② ────────────────────────────────────────────────────

export interface IssueRef { gate: string; refs: string[]; message?: string }

const sameSet = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

/**
 * Tra id chip của BE cho các vi phạm FE đang hiển thị (FE KHÔNG tự tính id). Khớp `gate` + `message` (FE gộp refs theo gate|message ⇒ có thể ứng với nhiều chip);
 * không có message ⇒ khớp `gate` + tập refs bằng nhau. Không khớp ⇒ bỏ (khung điền câu hỏi thay vì tick, như luật P2b).
 */
export const resolveSkeletonIssueIds = (chips: ReadonlyArray<SkeletonIssueChip>, wanted: ReadonlyArray<IssueRef>): string[] => {
  const out: string[] = [];
  for (const w of wanted) {
    const hit = chips.filter((c) => c.gate === w.gate && (w.message !== undefined ? c.message === w.message : sameSet(c.refs, w.refs)));
    for (const c of hit) if (!out.includes(c.id)) out.push(c.id);
  }
  return out;
};

export const skeletonIssueQuestion = (v: { gate: string; message: string }): string =>
  `Khung xương có cảnh báo "${str(v.message).slice(0, 300)}". Giải thích ngắn vì sao và nên sửa thế nào.`;

export interface SkeletonAssistAction {
  label: typeof FIX_LABEL | 'Hỏi trợ lý';
  intent: AssistantRequest;
}

/**
 * Nút ở mỗi vi phạm của ②: có chip BE khớp (gate + message) ⇒ "Nhờ AI sửa (tốn AI)" gửi luôn (khung tick đúng id, FE không tự tính id); KHÔNG khớp hoặc chưa tải được
 * chip (`chips === null`) ⇒ "Hỏi trợ lý" (chỉ điền câu hỏi, không tick) — đúng luật chốt 2.
 */
export const skeletonAssistAction = (
  v: { gate: string; refs: string[]; message: string }, chips: ReadonlyArray<SkeletonIssueChip> | null | undefined,
): SkeletonAssistAction => {
  const suggestedText = skeletonIssueQuestion(v);
  const matched = chips ? resolveSkeletonIssueIds(chips, [{ gate: v.gate, refs: v.refs, message: v.message }]).length > 0 : false;
  return matched
    ? { label: FIX_LABEL, intent: { stageKey: 'skeleton', issueIds: [], issueRefs: [{ gate: v.gate, refs: v.refs, message: v.message }], suggestedText, autoSend: 'fix' } }
    : { label: 'Hỏi trợ lý', intent: { stageKey: 'skeleton', issueIds: [], suggestedText } };
};
